import { Injectable } from '@angular/core';
import { Store, StoreConfig } from '@datorama/akita';
import { OutboundOrder } from 'src/app/core/models/order/outboundOrder';
import { OrderSupplier } from 'src/app/core/models/order/supplier/orderSupplier';
import { FavouriteArticlesCatalog } from 'src/app/core/models/article/favouriteArticlesCatalog';
import { Contract } from 'src/app/core/models/contract/contract';

/**
 * State interface for Order Edit
 *
 * Manages the current order being edited (similar to salesrep OrderStore)
 */
export interface OrderEditState {
  // Order lifecycle (similar to salesrep)
  initialLoadedOrder: OutboundOrder | null;
  remoteOrderBaseline: OutboundOrder | null;
  remoteOrder: OutboundOrder | null;
  currentOrder: OutboundOrder | null;

  // Context
  currentSupplier: OrderSupplier | null;
  favouriteArticlesCatalog: FavouriteArticlesCatalog | null;

  // Catalog data (from resolver)
  catalog: any | null;
  categories: any[];
  resolverArticles: any[];
  catalogSource: 'customer' | 'supplier' | 'master' | null;
  catalogPath: string | null; // Firestore path to articles collection

  // Contract (NEW)
  contract: Contract | null;
  typesenseCollection: string | null;

  // Meta
  isSaving: boolean;
  unsavedChanges: boolean;
}

/**
 * Create initial order edit state
 */
export function createInitialOrderEditState(): OrderEditState {
  return {
    initialLoadedOrder: null,
    remoteOrderBaseline: null,
    remoteOrder: null,
    currentOrder: null,
    currentSupplier: null,
    favouriteArticlesCatalog: null,
    catalog: null,
    categories: [],
    resolverArticles: [],
    catalogSource: null,
    catalogPath: null,
    contract: null,
    typesenseCollection: null,
    isSaving: false,
    unsavedChanges: false,
  };
}

/**
 * Order Edit Store
 *
 * Manages state for order being edited
 * Pattern matches salesrep OrderStore
 */
@Injectable({ providedIn: 'root' })
@StoreConfig({ name: 'order-edit', resettable: true })
export class OrderEditStore extends Store<OrderEditState> {
  constructor() {
    super(createInitialOrderEditState());
    console.log('🏪 [OrderEditStore] Initialized');
  }

  /**
   * Set initial loaded order
   */
  setInitialLoadedOrder(order: OutboundOrder | null): void {
    this.update({ initialLoadedOrder: order });
  }

  /**
   * Set remote order baseline
   */
  setRemoteOrderBaseline(order: OutboundOrder | null): void {
    this.update({ remoteOrderBaseline: order });
  }

  /**
   * Set remote order (from real-time updates)
   */
  setRemoteOrder(order: OutboundOrder | null): void {
    this.update({ remoteOrder: order });
  }

  /**
   * Set current order (working copy)
   */
  setCurrentOrder(order: OutboundOrder | null): void {
    this.update({
      currentOrder: order ? new OutboundOrder(order) : null,
      unsavedChanges: false, // Reset when setting new order
    });
  }

  /**
   * Update current order (mark as changed)
   */
  updateCurrentOrder(order: OutboundOrder): void {
    this.update({
      currentOrder: new OutboundOrder(order),
      unsavedChanges: true,
    });
  }

  /**
   * Set current supplier
   */
  setCurrentSupplier(supplier: OrderSupplier | null): void {
    this.update({ currentSupplier: supplier });
  }

  /**
   * Set favourite articles catalog
   */
  setFavouriteArticlesCatalog(catalog: FavouriteArticlesCatalog | null): void {
    this.update({ favouriteArticlesCatalog: catalog });
  }

  /**
   * Set catalog data (from resolver)
   */
  setCatalogData(data: {
    catalog: any;
    categories: any[];
    resolverArticles: any[];
    catalogSource: 'customer' | 'supplier' | 'master';
    catalogPath?: string;
  }): void {
    this.update({
      catalog: data.catalog,
      categories: data.categories,
      resolverArticles: data.resolverArticles,
      catalogSource: data.catalogSource,
      catalogPath: data.catalogPath || null,
    });
  }

  /**
   * Set saving state
   */
  setSaving(isSaving: boolean): void {
    this.update({ isSaving });
  }

  /**
   * Set contract
   */
  setContract(contract: Contract | null): void {
    this.update({ contract });
  }

  /**
   * Set Typesense collection name
   */
  setTypesenseCollection(collection: string | null): void {
    this.update({ typesenseCollection: collection });
  }

  /**
   * Mark as saved (clear unsaved changes)
   */
  markAsSaved(): void {
    this.update({
      unsavedChanges: false,
      isSaving: false,
    });
  }

  /**
   * Clear all state
   */
  clearState(): void {
    this.update(createInitialOrderEditState());
    console.log('🧹 [OrderEditStore] State cleared');
  }
}
